import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { TaskCatalogApiService } from '../../../services/task-catalog-api.service';
import { TaskCancelReasonRequest, TaskCancelReasonResponse } from '../../../models/task.model';
import { ToastService } from '../../../../../shared/services/toast.service';

@Component({
  selector: 'app-task-cancel-reasons-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './task-cancel-reasons-form.component.html',
})
export class TaskCancelReasonsFormComponent implements OnInit {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TaskCancelReasonResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  constructor(private fb: FormBuilder, private api: TaskCatalogApiService, private toast: ToastService) {
    this.initForm();
  }

  ngOnInit(): void {}

  // displayOrder no se edita aquí: el orden se define arrastrando en la lista.
  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(150)]],
      isActive: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.initForm();
  }

  loadItem(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.itemId = id;
    this.api.getCancelReasonById(id).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          isActive: item.isActive ?? true,
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el motivo';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    // Sin displayOrder: al crear el backend lo pone al final; al editar conserva el actual.
    const body: TaskCancelReasonRequest = {
      name: v.name.trim(),
      isActive: v.isActive ?? true,
    };

    const req$ = this.isEditing && this.itemId
      ? this.api.updateCancelReason(this.itemId, body)
      : this.api.createCancelReason(body);

    req$.subscribe({
      next: (item) => { this.saving = false; this.saved.emit(item); },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar el motivo de cancelación';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  cancelar(): void { this.cancelled.emit(); }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
